/**
 * Utility functions for cleaning up insurance calculator localStorage data
 * Provides 3 cleanup strategies for smooth user experience
 */

import { delocalizePath } from '@/lib/localizedRoutes.mjs';

const STORAGE_PREFIX = 'insurance_calculator_';
const EXPIRATION_MINUTES = 15; // Data expires after 15 minutes of last save
const CLEANUP_INTERVAL_MINUTES = 5; // Periodic sweep while app is open

/**
 * Get all insurance calculator storage keys
 */
export function getAllInsuranceStorageKeys() {
  if (typeof window === 'undefined') return [];
  
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      keys.push(key);
    }
  }
  return keys;
}

/** Remember which calculator step to show. Not part of the public URL. */
export function writeStoredCalculatorStep(insuranceSlug, stepNumber) {
  if (typeof window === 'undefined' || !insuranceSlug) return;
  const step = Number(stepNumber);
  const safeStep = Number.isInteger(step) && step >= 0 && step <= 10 ? step : 0;
  try {
    const storageKey = `${STORAGE_PREFIX}${insuranceSlug}`;
    const savedData = localStorage.getItem(storageKey);
    const parsed = savedData ? JSON.parse(savedData) : {};
    parsed.calculatorStep = safeStep;
    localStorage.setItem(storageKey, JSON.stringify(parsed));
  } catch (error) {
    console.error('[StorageCleanup] Error saving calculator step:', error);
  }
}

export function clearInsuranceStorage(insuranceSlug) {
  if (typeof window === 'undefined') return;
  
  try {
    const storageKey = `${STORAGE_PREFIX}${insuranceSlug}`;
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error('[StorageCleanup] Error clearing storage:', error);
  }
}

/**
 * Clear all insurance calculator storage
 */
export function clearAllInsuranceStorage() {
  if (typeof window === 'undefined') return;
  
  try {
    const keys = getAllInsuranceStorageKeys();
    keys.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('[StorageCleanup] Error clearing all storage:', error);
  }
}

/**
 * Add timestamp to stored data for expiration tracking
 */
export function addTimestampToStorage(data) {
  return {
    ...data,
    _storageTimestamp: Date.now(),
  };
}

/**
 * Persist that offers are being (re)calculated so a mid-calculation refresh can
 * redirect back to the additional-data step instead of restoring mixed prices.
 * Writes synchronously — use at recalculate start and in beforeunload.
 */
export function readOffersCalculationInterrupted(insuranceSlug) {
  if (typeof window === 'undefined' || !insuranceSlug) return false;

  try {
    const storageKey = `${STORAGE_PREFIX}${insuranceSlug}`;
    const savedData = localStorage.getItem(storageKey);
    if (!savedData) return false;
    return JSON.parse(savedData).quoteRecalculationInProgress === true;
  } catch {
    return false;
  }
}

export function persistOffersCalculationInterrupted(insuranceSlug, formData) {
  if (typeof window === 'undefined' || !insuranceSlug) return;

  try {
    const storageKey = `${STORAGE_PREFIX}${insuranceSlug}`;
    const savedData = localStorage.getItem(storageKey);
    const parsed = savedData ? JSON.parse(savedData) : {};
    const merged = { ...parsed, ...(formData || {}) };
    const {
      agree: _agree,
      agreed: _agreed,
      completedQuoteResponse: _cqr,
      selectedOffer: _so,
      selectedProviders: _sp,
      ...rest
    } = merged;

    localStorage.setItem(
      storageKey,
      JSON.stringify(
        addTimestampToStorage({
          ...rest,
          quoteRecalculationInProgress: true,
          completedQuoteResponse: null,
          selectedOffer: null,
          selectedProviders: [],
        })
      )
    );
  } catch (error) {
    console.error(
      '[StorageCleanup] Error persisting interrupted offers calculation:',
      error
    );
  }
}

/**
 * Get today's date formatted as YYYY-MM-DD (local timezone)
 * Used as minimum valid date when today is allowed for insurance start
 */
export function getTodayFormatted() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get tomorrow's date formatted as YYYY-MM-DD (local timezone)
 * Used when default start date should be tomorrow
 */
export function getTomorrowFormatted() {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if startDate is before today (invalid - must be at least today)
 * Today is allowed; only past dates are invalid
 */
export function isStartDateBeforeMinValid(startDateStr) {
  if (!startDateStr || typeof startDateStr !== 'string') return true;
  const today = getTodayFormatted();
  const startDateOnly = typeof startDateStr === 'string' ? startDateStr.split('T')[0] : startDateStr;
  return startDateOnly < today;
}

const DEFAULT_CALCULATOR_KEYS = new Set(['duration', 'usagePurpose']);
const META_STORAGE_KEYS = new Set(['_storageTimestamp', 'calculatorStep']);

function isMeaningfulStoredValue(val) {
  if (val === undefined || val === null || val === '') return false;
  if (typeof val === 'boolean' || typeof val === 'number') return true;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') return Object.keys(val).length > 0;
  return true;
}

/**
 * True if formData has any user/API progress beyond default duration + usage purpose.
 * Used to avoid staying on a high ?s= step when storage expired or was cleared.
 */
export function hasMeaningfulCalculatorData(data) {
  if (!data || typeof data !== 'object') return false;
  for (const key of Object.keys(data)) {
    if (DEFAULT_CALCULATOR_KEYS.has(key) || META_STORAGE_KEYS.has(key)) continue;
    if (isMeaningfulStoredValue(data[key])) return true;
  }
  return false;
}

/**
 * Check if stored data has expired
 */
export function isStorageExpired(data) {
  if (!data || !data._storageTimestamp) {
    return true; // If no timestamp, consider expired
  }
  
  const now = Date.now();
  const expirationTime = EXPIRATION_MINUTES * 60 * 1000;
  const isExpired = now - data._storageTimestamp > expirationTime;
  
  return isExpired;
}

/**
 * Clean expired storage entries (Strategy 2: Time-based expiration)
 */
export function cleanExpiredStorage() {
  if (typeof window === 'undefined') return;
  
  try {
    const keys = getAllInsuranceStorageKeys();
    let cleanedCount = 0;
    const clearedSlugs = [];
    
    keys.forEach(key => {
      try {
        const storedData = localStorage.getItem(key);
        if (storedData) {
          const parsed = JSON.parse(storedData);
          if (isStorageExpired(parsed)) {
            localStorage.removeItem(key);
            cleanedCount++;
            const slug = key.startsWith(STORAGE_PREFIX)
              ? key.slice(STORAGE_PREFIX.length)
              : null;
            if (slug) clearedSlugs.push(slug);
          }
        }
      } catch (error) {
        // If data is corrupted, remove it
        localStorage.removeItem(key);
        cleanedCount++;
        const slug = key.startsWith(STORAGE_PREFIX)
          ? key.slice(STORAGE_PREFIX.length)
          : null;
        if (slug) clearedSlugs.push(slug);
      }
    });

    if (clearedSlugs.length > 0) {
      window.dispatchEvent(
        new CustomEvent('insuranceCalculatorStorageCleared', {
          detail: { insuranceSlugs: [...new Set(clearedSlugs)] },
        })
      );
    }
    
    return cleanedCount;
  } catch (error) {
    console.error('[StorageCleanup] Error cleaning expired storage:', error);
    return 0;
  }
}

/**
 * Clear calculator data for one slug and travel quote UUID (used after checkout ends).
 */
export function clearStorageAfterPayment(insuranceSlug) {
  if (typeof window === 'undefined') return;
  
  try {
    clearInsuranceStorage(insuranceSlug);
    
    // Also clear travel quote UUID if exists
    localStorage.removeItem('travelQuoteRequestUuid');
  } catch (error) {
    console.error('[StorageCleanup] Error clearing storage after payment:', error);
  }
}

/**
 * After insurance checkout finishes (success, failure, or cancel), clear calculator localStorage.
 * Uses document.referrer to detect /skaiciuokle/{slug}; otherwise clears all calculator keys.
 */
export function clearInsuranceCalculatorStorageFromReferrer() {
  if (typeof window === 'undefined') return;

  try {
    const referrer = document.referrer || '';
    const referrerPath = referrer ? delocalizePath(new URL(referrer).pathname) : '';
    const insuranceMatch = referrerPath.match(/\/skaiciuokle\/([^/?]+)/);
    const insuranceSlug = insuranceMatch ? insuranceMatch[1] : null;

    if (insuranceSlug) {
      clearStorageAfterPayment(insuranceSlug);
    } else {
      clearAllInsuranceStorage();
      localStorage.removeItem('travelQuoteRequestUuid');
    }
  } catch (error) {
    console.error(
      '[StorageCleanup] Error clearing calculator storage from referrer:',
      error
    );
  }
}

/**
 * Clear storage when starting new quote (Strategy 3: Explicit new quote)
 */
export function clearStorageForNewQuote(insuranceSlug) {
  if (typeof window === 'undefined') return;
  
  try {
    clearInsuranceStorage(insuranceSlug);
  } catch (error) {
    console.error('[StorageCleanup] Error clearing storage for new quote:', error);
  }
}

/**
 * Initialize cleanup on app load - runs time-based cleanup
 * Returns cleanup function to clear interval if needed
 */
export function initializeStorageCleanup() {
  if (typeof window === 'undefined') return () => {};
  
  // Clean expired entries on app initialization
  cleanExpiredStorage();
  
  const intervalId = setInterval(() => {
    cleanExpiredStorage();
  }, CLEANUP_INTERVAL_MINUTES * 60 * 1000);
  
  // Return cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
  };
}

